import { AsyncUploader } from "components/AsyncUploader";
import { MprcImageSelectionDialog } from "components/MprcImageHandler";
import { MprcRequestIndicator } from "components/MprcRequestIndicator";
import { QuillCommands } from "components/Quill";
import ImageUrlBlot from "components/Quill/Blots/ImageUrlBlot";
import PerformaLinkBlot from "components/Quill/Blots/PerformaLinkBlot";
import ClipboardExt from "components/Quill/ClipboardExt";
import ImageDropAndPaste from "components/Quill/ImageDropAndPaste";
import ImageResize from "components/Quill/ImageResize";
import MagicUrl from "components/Quill/MagicUrl";
import { Mention, MentionBlot, publicationMentionConfig } from "components/Quill/Mention/publication";
import { MprcQuillEditor } from "components/Quill/MprcQuillEditor";
import { QuillProps } from "components/Quill/QuillProps";
import { AsyncUploadResult, TopicSelectorMode } from "generated/CoreApi_v1";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TopicSelectorHierarchicalDrawer } from "views/foundation/topic/drawers/TopicSelectorHierarchicalDrawer";
import { UserSelectorDrawer } from "views/foundation/user/drawers/UserSelectorDrawer";
import { ChannelSelectorDrawer } from "views/knowledge/channels/drawers/ChannelSelectorDrawer";
import { PublicationSelectorDrawer } from "views/knowledge/publication/drawers/PublicationSelectorDrawer";
import { TrainingSelectorDrawer } from "views/lms/training/drawers/TrainingSelectorDrawer";

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const MentionLinkDennotions = ["C", "P", "T"];

export type MentionDenotationChars = "@" | "#" | "C" | "P" | "T";

export type MentionData = {
  id: string;
  ot: "user" | "topic" | "channel" | "pub" | "training";
  value: string;
  denotationChar: MentionDenotationChars;
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const PublicationEditorComponent: React.ForwardRefRenderFunction<QuillCommands, Omit<QuillProps, "formats" | "modules" | "maxHeight">> = (props, forwardRef) => {
  const [dialog, setDialog] = useState<any>();
  const { t } = useTranslation("knowledge");

  const quillRef = React.useRef<QuillCommands>(null!);
  const runnning = React.useRef<boolean>(false);

  useImperativeHandle(forwardRef, () => ({
    ...quillRef.current
  }));

  const MentionOptions: Array<{ type: string, displayName: string, denotationChar: MentionDenotationChars; }> = useMemo(() => [
    { type: "people", displayName: t("Publication.Mention.User"), denotationChar: "@" },
    { type: "topic", displayName: t("Publication.Mention.Topic"), denotationChar: "#" },
    { type: "channel", displayName: t("Publication.Mention.Channel"), denotationChar: "C" },
    { type: "publication", displayName: t("Publication.Mention.Publication"), denotationChar: "P" },
    { type: "training", displayName: t("Publication.Mention.Training"), denotationChar: "T" }
  ], [t]);


  const initCustomMentions = () => {
    document.querySelectorAll('.ql-customMentions .ql-picker-label').forEach(target => {
      target.setAttribute('data-content', '@');
    });

    const placeholderPickerItems = document.querySelectorAll('.ql-customMentions .ql-picker-item');
    placeholderPickerItems.forEach(item => item.textContent = MentionOptions?.find(p => p.type == (item as any).dataset.value)?.displayName ?? "?");
  };

  const listenPerformaLink = useCallback((e: any) => {
    if (e.target && e.target.nodeName == "A" && e.target.classList && e.target.classList[0] == "ql-performa-link") {
      e.preventDefault();
    }
  }, []);

  const mentionsHandler = useCallback((value: string) => {
    var q = quillRef.current.getQuillRef();
    const range = q.getSelection(true);
    if (range == null) return;

    function continueInsertion(data: MentionData) {
      if (MentionLinkDennotions.indexOf(data.denotationChar) > -1) {
        q.insertEmbed(range.index, 'performaLink', data, "api");
        q.insertText(range.index + 1, ' ', "api");
        q.setSelection(range.index + 2, 1, "silent");
      }
      else {
        q.insertEmbed(range.index, 'mention', data, "api");
        q.insertText(range.index + 1, ' ', "api");
        q.setSelection(range.index + 2, 1, "silent");
      }
    }

    switch (value) {
      case "people":
        setDialog(
          <UserSelectorDrawer
            multi={false}
            onClose={(user) => {
              setDialog(null);
              if (user?.length == 1) { continueInsertion({ id: user[0].userId, ot: "user", value: user[0].name, denotationChar: "@" }); }
            }}
          />);
        break;
      case "topic":
        setDialog(
          <TopicSelectorHierarchicalDrawer
            mode={TopicSelectorMode.Normal}
            onClose={(topic) => {
              setDialog(null);
              if (topic && topic.length > 0) { continueInsertion({ id: topic[0].topicId, ot: "topic", value: topic[0].name, denotationChar: "#" }); }
            }}
          />);
        break;
      case "channel":
        setDialog(
          <ChannelSelectorDrawer
            multi={false}
            onClose={(channel) => {
              setDialog(null);
              if (channel?.length == 1) { continueInsertion({ id: channel[0].channelId, ot: "channel", value: channel[0].name, denotationChar: "C" }); }
            }}
          />);
        break;
      case "publication":
        setDialog(
          <PublicationSelectorDrawer
            multi={false}
            onClose={(pub) => {
              setDialog(null);
              if (pub?.length == 1) { continueInsertion({ id: pub[0].publicationId, ot: "pub", value: pub[0].title, denotationChar: "P" }); }
            }}
          />);
        break;
      case "training":
        setDialog(
          <TrainingSelectorDrawer
            multi={false}
            onClose={(pub) => {
              setDialog(null);
              if (pub?.length == 1) { continueInsertion({ id: pub[0].trainingId, ot: "training", value: pub[0].name, denotationChar: "T" }); }
            }}
          />);
        break;
    }
  }, []);

  const imageSelectionHandler = useCallback(() => {
    var q = quillRef.current.getQuillRef();
    const range = q.getSelection(true);
    if (range == null) return;
    setDialog(
      <MprcImageSelectionDialog
        acceptedFiles={{ "*/*": [] }}
        initialSource="library"
        backgroundType="solid"
        allowEdit={false}
        onClose={
          (image) => {
            setDialog(null);
            if (image != null) {
              const asyncUploader = new
                AsyncUploader({
                  onFileStarted: () => setDialog(<MprcRequestIndicator type="waiting" />),
                  onFileFinished: () => setDialog(null),
                  onFileUploadSuccess: (data: AsyncUploadResult) => {
                    q.insertEmbed(range.index, "image", data.mainFile?.url);
                    q.setSelection(range.index + 2, 1, "silent");
                    setDialog(null);
                  },
                  onFileUploadError: (inst, error) => {
                    enqueueSnackbar(t("AsyncUpload.UploadError", { ns: "shared" }));
                    setDialog(null);
                  }
                });

              asyncUploader.uploadImage(image.editedImage ?? image.source);
            }
          }
        }
      />
    );
  }, []);

  return <MprcQuillEditor
    {...props}
    ref={quillRef}
    modules={
      {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }, { align: [] }],
            ['link', 'image'],
            [{ 'customMentions': MentionOptions?.map(p => p.type) }],
            ['clean'],
          ],
          handlers: {
            image: imageSelectionHandler,
            customMentions: mentionsHandler
          }
        },
        imageDropAndPaste: {},
        imageResize: {},
        magicUrl: {},
        clipboardExt: {
          image: true,
          video: false,
          youtube: false,
        },
        clipboard: {
          // toggle to add extra line breaks when pasting HTML:
          matchVisual: true,
        },
        ...publicationMentionConfig

      }
    }
    formats={
      [
        'header', 'font', 'size', 'align', 'color', 'background',
        'bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block',
        'list', 'bullet', 'indent', 'sub', 'super', 'direction', 'image', 'link',
        // Custom
        'mention', 'performaLink'
      ]
    }
    onRegisterModules={(Quill: any) => {
      // For execute this line only once.
      Quill.register('modules/magicUrl', MagicUrl);
      Quill.register('modules/clipboardExt', ClipboardExt);
      Quill.register('modules/mention', Mention);
      Quill.register('modules/imageResize', ImageResize);
      Quill.register('modules/imageDropAndPaste', ImageDropAndPaste);
      // Blots
      Quill.register(ImageUrlBlot);
      Quill.register(MentionBlot);
      Quill.register(PerformaLinkBlot);

    }}
    onQuillReady={() => {
      initCustomMentions();
      document.body.addEventListener("click", listenPerformaLink);
      runnning.current = true;
    }}

    onUnmount={() => {
      if (runnning.current) {
        document.body.removeEventListener("click", listenPerformaLink);
        Array.from(document.body.getElementsByClassName("ql-mention-list-container")).forEach(el => el.remove());
      }
    }}
    maxHeight="100%"
    marginBottom="10px"
  >
    {dialog}
  </MprcQuillEditor>;
};

export const PublicationEditor = React.forwardRef(PublicationEditorComponent);
